import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { householdsApi } from '@/app/householdsApi';
import { addressesApi } from '@/features/addresses';
import { admissionClassesApi } from '@/features/admissionClasses/admissionClassesApi';
import { appSettingsApi } from '@/features/appSettings/appSettingsApi';
import authSlice from '@/features/auth/authSlice';
import { departmentsApi } from '@/features/departments/departmentsApi';
import { employeePositionApi } from '@/features/employeePositions/employeePositionsApi';
import { employeesApi } from '@/features/employees/employeesApi';
import { equipmentGroupApi } from '@/features/equipmentGroupApi';
import { equipmentManufacturerApi } from '@/features/equipmentManufacturerApi';
import { equipmentTypeApi } from '@/features/equipmentTypeApi';
import { gasConsumingEquipmentApi } from '@/features/gasConsumingEquipmentApi';
import { gasPipelineSectionsApi } from '@/features/gasPipelineSectionsApi';
import { locationsApi } from '@/features/locations/locationsApi';
import { measurementRangesApi } from '@/features/measurementRanges/measurementRangesApi';
import { meterLocationsApi } from '@/features/meterLocations/meterLocationsApi';
import { meterThroughputsApi } from '@/features/meterThroughputs/meterThroughputsApi';
import { meteringDeviceGroupsApi } from '@/features/meteringDeviceGroups/meteringDeviceGroups';
import { meteringDevicePassportTypeApi } from '@/features/meteringDevicePassportTypes/meteringDevicePassportTypesApi';
import { meteringDeviceTypesApi } from '@/features/meteringDeviceTypes/meteringDeviceTypes';
import { meteringNodeApi } from '@/features/meteringNode/meteringNodeApi';
import { meteringNodeCommercialApi } from '@/features/meteringNodeCommercial/meteringNodeCommercialApi';
import { meteringNodeKindApi } from '@/features/meteringNodeKind/meteringNodeKindApi';
import { meteringNodeLocationTypeApi } from '@/features/meteringNodeLocationType/meteringNodeLocationTypeApi';
import { meteringSealApi } from '@/features/meteringSealApi';
import { nodeSealLocationApi } from '@/features/nodeSealLocation/nodeSealLocationApi';
import { nodeSealTypesApi } from '@/features/nodeSealTypes/nodeSealTypesApi';
import { organizationsApi } from '@/features/organizations/organizationsApi';
import { rolesApi } from '@/features/roles/rolesApi';
import { seasonTypesApi } from '@/features/seasonTypes/seasonTypesApi';
import { serviceOrganizationsApi } from '@/features/serviceOrganizationsApi';
import { subdivisionsApi } from '@/features/subdivisions/subdivisionsApi';
import uiSlice from '@/features/ui/uiSlice';

const rootReducer = combineReducers({
  [householdsApi.reducerPath]: householdsApi.reducer,
  [uiSlice.name]: uiSlice.reducer,
  [appSettingsApi.reducerPath]: appSettingsApi.reducer,
  [authSlice.name]: authSlice.reducer,
  [organizationsApi.reducerPath]: organizationsApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [employeesApi.reducerPath]: employeesApi.reducer,
  [departmentsApi.reducerPath]: departmentsApi.reducer,
  [employeePositionApi.reducerPath]: employeePositionApi.reducer,
  [locationsApi.reducerPath]: locationsApi.reducer,
  [meteringDeviceGroupsApi.reducerPath]: meteringDeviceGroupsApi.reducer,
  [meteringDeviceTypesApi.reducerPath]: meteringDeviceTypesApi.reducer,
  [meteringNodeApi.reducerPath]: meteringNodeApi.reducer,
  [meteringNodeCommercialApi.reducerPath]: meteringNodeCommercialApi.reducer,
  [meteringNodeKindApi.reducerPath]: meteringNodeKindApi.reducer,
  [meteringNodeLocationTypeApi.reducerPath]:
    meteringNodeLocationTypeApi.reducer,
  [seasonTypesApi.reducerPath]: seasonTypesApi.reducer,
  [equipmentManufacturerApi.reducerPath]: equipmentManufacturerApi.reducer,
  [equipmentTypeApi.reducerPath]: equipmentTypeApi.reducer,
  [equipmentGroupApi.reducerPath]: equipmentGroupApi.reducer,
  [gasConsumingEquipmentApi.reducerPath]: gasConsumingEquipmentApi.reducer,
  [serviceOrganizationsApi.reducerPath]: serviceOrganizationsApi.reducer,
  [subdivisionsApi.reducerPath]: subdivisionsApi.reducer,
  [meterThroughputsApi.reducerPath]: meterThroughputsApi.reducer,
  [measurementRangesApi.reducerPath]: measurementRangesApi.reducer,
  [meterLocationsApi.reducerPath]: meterLocationsApi.reducer,
  [gasPipelineSectionsApi.reducerPath]: gasPipelineSectionsApi.reducer,
  [nodeSealLocationApi.reducerPath]: nodeSealLocationApi.reducer,
  [nodeSealTypesApi.reducerPath]: nodeSealTypesApi.reducer,
  [meteringSealApi.reducerPath]: meteringSealApi.reducer,
  [meteringDevicePassportTypeApi.reducerPath]:
    meteringDevicePassportTypeApi.reducer,
  [admissionClassesApi.reducerPath]: admissionClassesApi.reducer,
  [addressesApi.reducerPath]: addressesApi.reducer,
});

const middlewares = [
  householdsApi.middleware,
  appSettingsApi.middleware,
  organizationsApi.middleware,
  rolesApi.middleware,
  employeesApi.middleware,
  departmentsApi.middleware,
  employeePositionApi.middleware,
  locationsApi.middleware,
  meteringDeviceGroupsApi.middleware,
  equipmentManufacturerApi.middleware,
  equipmentTypeApi.middleware,
  equipmentGroupApi.middleware,
  gasConsumingEquipmentApi.middleware,
  meteringDeviceTypesApi.middleware,
  meteringNodeApi.middleware,
  meteringNodeCommercialApi.middleware,
  meteringNodeKindApi.middleware,
  meteringNodeLocationTypeApi.middleware,
  seasonTypesApi.middleware,
  meterThroughputsApi.middleware,
  measurementRangesApi.middleware,
  meterLocationsApi.middleware,
  serviceOrganizationsApi.middleware,
  subdivisionsApi.middleware,
  gasPipelineSectionsApi.middleware,
  nodeSealLocationApi.middleware,
  nodeSealTypesApi.middleware,
  meteringSealApi.middleware,
  meteringDevicePassportTypeApi.middleware,
  admissionClassesApi.middleware,
  addressesApi.middleware,
];

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(middlewares),
});

export default store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
